/**
 * Access to AbridgeSharedUi — Abridge's real shared design system.
 *
 * The bundle in public/abridge-shared-ui.js is the exact artifact Claude Design
 * emitted for the CDP portal mock. It is an esbuild IIFE that assigns
 * `window.AbridgeSharedUi` and resolves React through two shims:
 *
 *     shim:react-shim      -> window.React
 *     shim:react-dom-shim  -> window.ReactDOM
 *
 * So we must publish those globals BEFORE the bundle parses. `loadDesignSystem`
 * does exactly that, then injects the script and waits for it. Because the
 * globals are the same module instances the app imports from npm, there is only
 * ever one React in the page — hooks, context and HMR all behave normally.
 *
 * When shared-ui is published to Abridge's internal npm registry, delete this
 * file and the public/ bundle and replace the imports in components with
 * `import { Button } from '@abridge/shared-ui'`. Nothing else has to change:
 * every component below is re-exported under its real name.
 */
import type { ComponentType, ReactElement, ReactNode, ChangeEvent, CSSProperties } from 'react';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { jsx, jsxs } from 'react/jsx-runtime';

/* ------------------------------------------------------------------ *
 * Prop types — transcribed from the bundled component implementations
 * ------------------------------------------------------------------ */

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outlined'
  | 'tertiary'
  | 'accent'
  | 'danger'
  | 'icon'
  | 'icon-danger';

export type TagVariant = 'default' | 'info' | 'warning' | 'success' | 'danger';
export type AlertVariant = 'default' | 'info' | 'danger' | 'warning' | 'success';

export interface ButtonProps {
  variant?: ButtonVariant;
  size?: 'default' | 'sm' | 'lg';
  leftIcon?: IconName;
  rightIcon?: IconName;
  loading?: boolean;
  disabled?: boolean;
  onPress?: () => void;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  'aria-label'?: string;
}

export interface TagProps {
  variant?: TagVariant;
  size?: 'sm' | 'default';
  selected?: boolean;
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  onPress?: () => void;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  'aria-label'?: string;
}

export interface IconProps {
  name: IconName;
  size?: 'sm' | 'md' | 'lg' | number;
  color?: string;
  className?: string;
  /** Supplying a label makes the icon an img role; omitting it hides it from AT. */
  label?: string;
}

export interface InputProps {
  value?: string;
  defaultValue?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  size?: 'sm' | 'md';
  id?: string;
  type?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
}

export interface FormFieldProps {
  label?: string;
  helperText?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  controlId?: string;
  size?: 'sm' | 'md';
  className?: string;
  children?: ReactNode;
}

export interface SwitchProps {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  variant?: 'status' | 'mode';
  size?: 'default' | 'sm';
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
}

export interface TooltipProps {
  open?: boolean;
  children: ReactElement;
  content: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  openDelay?: number;
}

export interface AlertProps {
  variant?: AlertVariant;
  size?: 'default' | 'compact';
  showIcon?: boolean;
  onDismiss?: () => void;
  className?: string;
  children?: ReactNode;
}

/**
 * The icon names the portal uses, verified present in the bundle's iconMap.
 *
 * NOTE: the Design mock referenced placeholder names that do NOT exist in the
 * real design system (`home`, `plus`, `search`, `bell`, `settings`, `note`,
 * `worklist`, `coding`, `browser`, `folder`, `shield`). src/icons.ts maps the
 * mock's intent onto these real names.
 */
export type IconName =
  | 'home-filled'
  | 'note-stacked'
  | 'note-alt'
  | 'note-settings'
  | 'plus-circle'
  | 'bell-filled'
  | 'filter-parameters'
  | 'folder-filled'
  | 'service-summary'
  | 'single-feedback'
  | 'chat'
  | 'cloud-success'
  | 'account-circle'
  | 'chevron-right'
  | 'chevron-down'
  | 'arrow-left'
  | 'arrow-right'
  | 'check-circle'
  | 'check-circle-filled'
  | 'close-circle-filled'
  | 'error-filled'
  | 'edit'
  | 'warning-filled'
  | 'info-filled'
  | 'log-in'
  | 'log-out'
  | 'open-in-new'
  | 'visibility-on'
  | 'visibility-off'
  | 'status-not-started'
  | 'close-bold';

/**
 * The cva function behind `Input`'s styling. The design system exports it
 * because `Select` and `Combobox` both need a *wrapper* element that looks
 * exactly like a text input, and reuse it rather than duplicating the classes.
 * `RepoNameField` composes a fixed prefix the same way.
 */
export type InputVariants = (options?: {
  size?: 'sm' | 'md';
  invalid?: boolean;
}) => string;

interface AbridgeSharedUiNamespace {
  Button: ComponentType<ButtonProps>;
  Tag: ComponentType<TagProps>;
  Icon: ComponentType<IconProps>;
  Input: ComponentType<InputProps>;
  FormField: ComponentType<FormFieldProps>;
  Switch: ComponentType<SwitchProps>;
  Tooltip: ComponentType<TooltipProps>;
  Alert: ComponentType<AlertProps>;
  inputVariants: InputVariants;
  iconNames: string[];
}

declare global {
  interface Window {
    React?: typeof React;
    ReactDOM?: typeof ReactDOM;
    AbridgeSharedUi?: AbridgeSharedUiNamespace;
  }
}

const BUNDLE_URL = '/abridge-shared-ui.js';

let loader: Promise<AbridgeSharedUiNamespace> | undefined;

export function loadDesignSystem(): Promise<AbridgeSharedUiNamespace> {
  if (loader) return loader;

  loader = new Promise((resolve, reject) => {
    if (window.AbridgeSharedUi) {
      resolve(window.AbridgeSharedUi);
      return;
    }

    // Must be set before the bundle parses — its shims capture these eagerly.
    // The vendored bundle also mutates this object to add jsx/jsxs, so do not
    // expose React's immutable ESM namespace object directly.
    window.React = {
      ...React,
      jsx,
      jsxs,
      jsxDEV: jsx,
      Fragment: React.Fragment,
    } as typeof React;
    window.ReactDOM = ReactDOM;

    const script = document.createElement('script');
    script.src = BUNDLE_URL;
    script.async = false;
    script.onload = () => {
      const ns = window.AbridgeSharedUi;
      if (!ns) {
        reject(new Error(`${BUNDLE_URL} loaded but did not define window.AbridgeSharedUi`));
        return;
      }
      resolve(ns);
    };
    script.onerror = () =>
      reject(new Error(`Failed to load the AbridgeSharedUi bundle from ${BUNDLE_URL}`));
    document.head.appendChild(script);
  });

  return loader;
}

/**
 * Synchronous namespace accessor for use inside components. Safe because
 * `loadDesignSystem()` is awaited in main.tsx before the first render.
 */
function ns(): AbridgeSharedUiNamespace {
  const value = window.AbridgeSharedUi;
  if (!value) {
    throw new Error('AbridgeSharedUi is not loaded — await loadDesignSystem() before rendering.');
  }
  return value;
}

/* Thin pass-through wrappers so screens read as ordinary component imports. */
export const Button = (props: ButtonProps) => React.createElement(ns().Button, props);
export const Tag = (props: TagProps) => React.createElement(ns().Tag, props);
export const Icon = (props: IconProps) => React.createElement(ns().Icon, props);
export const Input = (props: InputProps) => React.createElement(ns().Input, props);
export const FormField = (props: FormFieldProps) => React.createElement(ns().FormField, props);
export const Switch = (props: SwitchProps) => React.createElement(ns().Switch, props);
export const Tooltip = (props: TooltipProps) => React.createElement(ns().Tooltip, props);
export const Alert = (props: AlertProps) => React.createElement(ns().Alert, props);

export const inputVariants: InputVariants = options => ns().inputVariants(options);
